export default function TitleCompanySection() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="inline-block bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              Title Company Protection
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Every Transaction Handled Through a Title Company
            </h2>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              When you sell my apartments to T-Rex Real Estate, you gain the security and protection of working through a reputable title company. This is not optional—it's how we do business on every single transaction, regardless of size.
            </p>
            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              Title companies serve as neutral third parties that protect both buyers and sellers. They conduct thorough title searches, resolve any liens or encumbrances, hold funds in escrow, prepare all legal documents, and ensure the transaction complies with Texas real estate law.
            </p>

            <div className="bg-gray-50 rounded-2xl p-8 mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">What the Title Company Does:</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-search-line text-white"></i>
                  </div>
                  <div>
                    <div className="font-bold text-gray-900 mb-1">Title Search & Examination</div>
                    <div className="text-gray-600">Comprehensive search of public records to verify ownership and identify any liens, judgments, or encumbrances</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-white"></i>
                  </div>
                  <div>
                    <div className="font-bold text-gray-900 mb-1">Title Insurance</div>
                    <div className="text-gray-600">Issues title insurance policies protecting against future claims or defects in the title</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-file-text-line text-white"></i>
                  </div>
                  <div>
                    <div className="font-bold text-gray-900 mb-1">Document Preparation</div>
                    <div className="text-gray-600">Prepares all closing documents including deeds, settlement statements, and transfer documents</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-bank-line text-white"></i>
                  </div>
                  <div>
                    <div className="font-bold text-gray-900 mb-1">Escrow Services</div>
                    <div className="text-gray-600">Holds funds in escrow and disburses payments to all parties according to closing instructions</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-scales-3-line text-white"></i>
                  </div>
                  <div>
                    <div className="font-bold text-gray-900 mb-1">Lien Resolution</div>
                    <div className="text-gray-600">Coordinates payoff of existing mortgages, liens, and other encumbrances from sale proceeds</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-file-list-3-line text-white"></i>
                  </div>
                  <div>
                    <div className="font-bold text-gray-900 mb-1">Recording & Filing</div>
                    <div className="text-gray-600">Records the deed and other documents with the county to complete the legal transfer of ownership</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-r-xl">
              <div className="flex items-start space-x-3">
                <i className="ri-information-fill text-teal-600 text-2xl flex-shrink-0"></i>
                <div>
                  <div className="font-bold text-gray-900 mb-2">Your Protection Guaranteed</div>
                  <div className="text-gray-700">Using a title company ensures you receive clear title, proper documentation, and secure fund transfers. It's the professional, safe way to sell my apartments.</div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-8">
            <div className="relative">
              <img 
                src="https://public.readdy.ai/ai/img_res/b9b22e0df3c5f852b005651f6f2a2227.jpg"
                alt="Title Company Closing Process"
                className="rounded-2xl shadow-2xl w-full h-full object-cover object-top"
              />
            </div>

            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-6">Why We Always Use Title Companies</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <i className="ri-shield-star-fill text-teal-400 text-xl flex-shrink-0 mt-1"></i>
                  <span><strong>Seller Protection:</strong> Ensures you receive full payment and proper documentation</span>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-shield-star-fill text-teal-400 text-xl flex-shrink-0 mt-1"></i>
                  <span><strong>Legal Compliance:</strong> All documents prepared according to Texas real estate law</span>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-shield-star-fill text-teal-400 text-xl flex-shrink-0 mt-1"></i>
                  <span><strong>Neutral Third Party:</strong> Unbiased professional handling the transaction details</span>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-shield-star-fill text-teal-400 text-xl flex-shrink-0 mt-1"></i>
                  <span><strong>Peace of Mind:</strong> Know that every detail is handled correctly and professionally</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}